import React, { useContext, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { Banner, BannerHeader, BannerNote } from '@ferris/react/components/banner';
import { TrackingContext } from '@vrbo/react-event-tracking';
import TranslationUtil from '../../../utils/TranslationUtil';
import LeadActions from '../../../actions/lead';
import { EMAIL_DUP_ERROR_MESSAGE, EMAIL_DUP_TRAVELER_ERROR_MESSAGE, PHONE_VALIDATION_ERROR_MESSAGE } from '../../../constants/common';

// eslint-disable-next-line max-len
const isDuplicateOwnerAccountError = (error) => error && error.indexOf(EMAIL_DUP_ERROR_MESSAGE) !== -1;
// eslint-disable-next-line max-len
const isDuplicateTravelerAccountError = (error) => error && error.indexOf(EMAIL_DUP_TRAVELER_ERROR_MESSAGE) !== -1;
const isInvalidPhoneNumber = (error) => error && error.indexOf(PHONE_VALIDATION_ERROR_MESSAGE) !== -1;

function LeadCreateErrorAlert(props) {
    const [firedDuplicateError, setFiredDuplicateError] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const { trigger } = useContext(TrackingContext);
    const getPropsFromState = (state) => {
        const { authUrl } = props;
        const {
            translations,
            fallbackTranslations,
            createAccount,
            rootPath,
            locale
        } = state.app;
        const translationUtil = new TranslationUtil(translations, fallbackTranslations);
        const getTranslation = (key, params) => translationUtil.getTranslation(key, params);
        let duplicateType = null;
        let phoneInvalidType = null;

        let errorMessage = getTranslation('lyp.leadForm.errorMessage');
        if (isDuplicateOwnerAccountError(createAccount.error)) {
            errorMessage = getTranslation(`Vrbo.earn.validation.${EMAIL_DUP_ERROR_MESSAGE}`, { authUrl });
            duplicateType = 'owner';
        }
        if (isDuplicateTravelerAccountError(createAccount.error)) {
            errorMessage = getTranslation(`Vrbo.earn.validation.${EMAIL_DUP_TRAVELER_ERROR_MESSAGE}`, { authUrl });
            duplicateType = 'traveler';
        }
        if (isInvalidPhoneNumber(createAccount.error)) {
            errorMessage = getTranslation(PHONE_VALIDATION_ERROR_MESSAGE);
            phoneInvalidType = true;
        }

        return {
            title: getTranslation('lyp.leadForm.errorTitle'),
            errorMessage,
            duplicateType,
            phoneInvalidType,
            rootPath,
            locale: locale?.canonical
        };
    };

    const {
        title, errorMessage, duplicateType, phoneInvalidType, locale, rootPath
    } = useSelector(getPropsFromState, shallowEqual);
    const { isConventionalLodgingFlow, isDismissable } = props;

    const dispatch = useDispatch();
    const onDismiss = () => {
        setDismissed(true);
        dispatch(LeadActions.setLeadCreationError(false));
    };

    if (duplicateType && !firedDuplicateError) {
        setFiredDuplicateError(true);
        if (duplicateType === 'owner') {
            trigger('genericEvent', { category: 'duplicate account', action: 'owner' });
        }
        if (duplicateType === 'traveler') {
            trigger('genericEvent', { category: 'duplicate account', action: 'traveler' });
        }
    }
    let urlToHomePage;

    let appPath = rootPath;
    if (!appPath && typeof window !== 'undefined') {
        const { pathname } = window.location;
        const segments = pathname.split('/').filter(Boolean);
        appPath = segments.length > 1 ? segments[1] : 'list';
    }
    // Fallback to 'list' if still no appPath
    appPath = appPath || 'list';

    if (isConventionalLodgingFlow) {
        urlToHomePage = `/${locale}/${appPath}`;
    } else {
        const vrboLocale = locale?.toLowerCase().split('_').join('-');
        urlToHomePage = `/${vrboLocale}/${appPath}`;
    }

    return (
        <div>
            { !dismissed && (
                <Banner
                    classes="lead-form-banner"
                    type="error"
                    dismissAltText="Close"
                    dismiss={isDismissable}
                    onDismiss={onDismiss}
                    scope="page"
                    stacked
                >
                    <BannerHeader>
                        {title}
                    </BannerHeader>
                    <BannerNote>

                        {duplicateType || phoneInvalidType ? (
                            // eslint-disable-next-line react/no-danger
                            <p dangerouslySetInnerHTML={{ __html: errorMessage }} />
                        ) : (
                            <a href={urlToHomePage}>
                                {/* eslint-disable-next-line react/no-danger */}
                                <p dangerouslySetInnerHTML={{ __html: errorMessage }} />
                            </a>
                        )}
                    </BannerNote>

                </Banner>
            )}
        </div>
    );
}

LeadCreateErrorAlert.propTypes = {
    authUrl: PropTypes.string,
    isDismissable: PropTypes.bool,
    isConventionalLodgingFlow: PropTypes.bool.isRequired
};

LeadCreateErrorAlert.defaultProps = {
    authUrl: '',
    isDismissable: true
};

export default LeadCreateErrorAlert;
